import { useMemo } from 'react'

import { matchFilterNumber, matchFilterString, sortArrayBy } from '@domain-group/javascript-utils'

import { useLoadMore, useWatchEntityUpdates } from '@domain-group/react-hooks'

import useReduxAction from '@hooks/useReduxAction'
import useSelector from '@hooks/useSelector'

import type { ProviderPriceListItemOptionModel, ProviderPriceListItemOptionRequestOptions } from '@models/types'
import type { ModuleState } from '@redux/modules/types'

const watchEntityKeys = ['providerPriceListItemOptions']

type ProviderPriceListItemOptionFilters = {
  provider_price_list_item_id?: number,
  string?: string,
}

type UseProviderPriceListItemOptionsOptions = {
  filters?: ProviderPriceListItemOptionFilters,
  performHttpRequests?: boolean,
  requestOptions?: ProviderPriceListItemOptionRequestOptions,
}

function useProviderPriceListItemOptions(options: UseProviderPriceListItemOptionsOptions) {
  const { filters = {}, requestOptions } = options
  const {
    provider_price_list_item_id: filterProviderPriceListItemId,
    string: filterString,
  } = filters

  const {
    updatedEntities: { providerPriceListItemOptions: providerPriceListItemOptionsUpdatedAt },
  } = useWatchEntityUpdates(watchEntityKeys)

  const { providerPriceListItemOptions } = useSelector(reduxState => reduxState.entities)

  const filteredProviderPriceListItemOptions = useMemo(() => {
    const filtered = Object.values(providerPriceListItemOptions)
      .filter((providerPriceListItemOption: ProviderPriceListItemOptionModel) => {
        const { id, provider_price_list_item_id, title } = providerPriceListItemOption

        const matchId = id.toString() === filterString
        const matchTitle = matchFilterString(title, filterString)
        const matchString = matchId || matchTitle

        const matchProviderPriceListItemId = matchFilterNumber(provider_price_list_item_id, filterProviderPriceListItemId)

        return matchProviderPriceListItemId && matchString
      })

    return sortArrayBy(filtered, 'asc', 'title')
  }, [providerPriceListItemOptionsUpdatedAt, JSON.stringify(filters)])

  const filteredProviderPriceListItemOptionsCount = filteredProviderPriceListItemOptions.length
  const hasFilteredProviderPriceListItemOptions = !!filteredProviderPriceListItemOptionsCount

  const loadMorePayload = useLoadMore({
    filters,
    loadedCount: filteredProviderPriceListItemOptionsCount,
    performHttpRequests: options.performHttpRequests,
  })

  const {
    callbacks: { loadMore },
    canLoadMore,
    filtersWithOffset,
    limit,
    performHttpRequests,
  } = loadMorePayload

  const { loading: loadingProviderPriceListItemOptions } = useReduxAction(
    'providerPriceListItemOptions',
    'loadProviderPriceListItemOptions',
    {
      ...requestOptions,
      ...filtersWithOffset,
      limit,
    },
    [filtersWithOffset, performHttpRequests],
    {
      shouldPerformFn: ({ loading }: ModuleState) => performHttpRequests && !loading,
    },
  )

  return {
    callbacks: {
      loadMore,
    },
    canLoadMore,
    filteredProviderPriceListItemOptions,
    filteredProviderPriceListItemOptionsCount,
    hasFilteredProviderPriceListItemOptions,
    loading: loadingProviderPriceListItemOptions,
  }
}

export default useProviderPriceListItemOptions
